import React, { useEffect, useState } from "react";
import { fmtDate } from "@/lib/dates";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicNav, PublicFooter } from "@/components/PublicChrome";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { openAskJane } from "@/components/AdvisorWidget";
import { CheckCircle2, BedDouble, Users, GraduationCap, ArrowRight, Mail, Scale } from "lucide-react";
import { EmailJaneLink } from "@/components/EmailJaneLink";

const RISE = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 } };
const RISE_SM = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };
const ONCE = { once: true };

const INCLUDED = [
  "Unlimited staff logins", "Full on-demand Essential Oral Care training", "Automatically issued certificates",
  "CQC evidence pack export & audit", "Live Oral Champion booking", "Full resources library",
  "Reminder emails", "Access for new starters throughout the year",
];

export const FALLBACK_BANDS = {
  intro_active: new Date() <= new Date("2026-11-11T23:59:59Z"),
  intro_deadline: "2026-11-11T23:59:59+00:00",
  intro_max_saving: 100,
  bands: [
    { key: "beds_25", label: "Up to 25 beds", intro: 249, renewal: 299, annual_lookup: "beds_25_annual" },
    { key: "beds_50", label: "26–50 beds", intro: 299, renewal: 399, annual_lookup: "beds_50_annual" },
    { key: "beds_75", label: "51–75 beds", intro: 399, renewal: 499, annual_lookup: "beds_75_annual" },
    { key: "beds_100", label: "76–100 beds", intro: 499, renewal: 599, annual_lookup: "beds_100_annual" },
    { key: "beds_150", label: "101–150 beds", intro: 599, renewal: 699, annual_lookup: "beds_150_annual" },
  ],
};

const gbp = (n) => `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: Number.isInteger(Number(n)) ? 0 : 2, maximumFractionDigits: 2 })}`;
const deadlineText = (iso) => fmtDate(iso);

function BandRow({ band, intro, i, onStart }) {
  return (
    <motion.div initial={RISE.initial} animate={RISE.animate} transition={{ delay: 0.05 * i }}
      data-testid={`tier-${band.key}`}
      className="grid grid-cols-2 md:grid-cols-[1.4fr_1fr_1fr_auto] gap-x-6 gap-y-3 items-center px-5 md:px-7 py-5 border-t border-cream-deep hover:bg-cream/60 transition-colors duration-200">
      <div className="col-span-2 md:col-span-1 flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-brand-light grid place-items-center flex-shrink-0"><BedDouble className="h-4 w-4 text-brand" /></div>
        <span className="font-semibold text-brand">{band.label}</span>
      </div>
      <div>
        <div className="label-caps text-muted-foreground md:hidden mb-1">{intro ? "First year" : "Per year"}</div>
        <span data-testid={`tier-${band.key}-price`} className="text-2xl md:text-3xl font-bold font-heading text-coral">
          {gbp(intro ? band.intro : band.renewal)}
        </span>
      </div>
      <div>
        <div className="label-caps text-muted-foreground md:hidden mb-1">Standard renewal</div>
        <span data-testid={`tier-${band.key}-renewal`} className="text-lg font-semibold text-brand/70">{gbp(band.renewal)}/year</span>
      </div>
      <div className="col-span-2 md:col-span-1">
        <Button data-testid={`tier-${band.key}-btn`} onClick={onStart} className="w-full md:w-auto rounded-full bg-brand hover:bg-brand-hover text-white h-10 px-5">Start free trial</Button>
      </div>
    </motion.div>
  );
}

function ContactRow() {
  return (
    <div data-testid="tier-151-plus" className="grid grid-cols-2 md:grid-cols-[1.4fr_1fr_1fr_auto] gap-x-6 gap-y-3 items-center px-5 md:px-7 py-5 border-t border-cream-deep bg-brand-light/40">
      <div className="col-span-2 md:col-span-1 flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-brand grid place-items-center flex-shrink-0"><BedDouble className="h-4 w-4 text-white" /></div>
        <span className="font-semibold text-brand">151+ beds</span>
      </div>
      <div className="text-lg font-semibold text-brand">Bespoke</div>
      <div className="text-sm text-muted-foreground">Tailored to your group</div>
      <div className="col-span-2 md:col-span-1 text-left md:text-right">
        <Button data-testid="tier-151-contact-btn" variant="outline" onClick={() => openAskJane("Hi Jane, we have 151+ registered beds and would like a bespoke Care Home Licence quote. Our care home is: ")}
          className="w-full md:w-auto rounded-full border-brand text-brand hover:bg-brand hover:text-white h-10 px-5 transition-colors duration-200">Contact us</Button>
        <a data-testid="tier-151-email" href="mailto:jane@kohc.co.uk" className="mt-1.5 text-xs text-muted-foreground hover:text-coral flex md:justify-end items-center gap-1"><Mail className="h-3 w-3" />jane@kohc.co.uk</a>
      </div>
    </div>
  );
}

export default function Pricing() {
  const nav = useNavigate();
  const [data, setData] = useState(FALLBACK_BANDS);
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    api.get("/billing/bands").then((r) => { if (r.data?.bands?.length) setData(r.data); }).catch(() => {});
    api.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);
  const tt = plans.find((p) => p.lookup_key === "tt_course");
  const intro = !!data?.intro_active;
  const deadline = data ? deadlineText(data.intro_deadline) : "11 November 2026";
  const saving = data ? gbp(data.intro_max_saving) : "£100";
  const fromPrice = data ? gbp(intro ? data.bands[0].intro : data.bands[0].renewal) : "£249";
  const start = () => nav("/signup");

  return (
    <div className="min-h-screen bg-cream font-body">
      <PublicNav />

      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-16 pb-10">
        <span className="label-caps text-coral">Care Home Licence</span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-brand tracking-tight mt-3 max-w-3xl">Your whole team. One simple licence.</h1>
        <p className="text-base md:text-lg text-muted-foreground mt-5 max-w-2xl">Give your entire care team access to KOHC's Essential Oral Care training, practical resources and ongoing support.</p>
        <p className="mt-3 font-semibold text-brand">Unlimited staff. One care home. One annual licence.</p>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-20">
        {intro && (
          <div data-testid="intro-offer-banner" className="rounded-2xl bg-coral text-white px-6 py-4 flex flex-wrap items-center gap-x-6 gap-y-2 mb-6">
            <span className="label-caps text-white/90">Introductory offer for new care homes</span>
            <span className="text-sm md:text-base font-medium">Join KOHC by {deadline} and receive our introductory first-year rate — save up to {saving} after your 14-day free trial.</span>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          <p data-testid="billing-note" className="text-sm text-muted-foreground">One annual licence per care home · billed once a year · your standard renewal price is shown upfront.</p>
          <p className="text-xs text-muted-foreground">Need to spread the cost? <EmailJaneLink testId="instalments-link">Email Jane about paying in two instalments</EmailJaneLink>.</p>
        </div>

        <div data-testid="pricing-table" className="rounded-3xl bg-cream-card border border-cream-deep shadow-sm overflow-hidden">
          <div className="hidden md:grid grid-cols-[1.4fr_1fr_1fr_auto] gap-x-6 px-7 py-4 label-caps text-muted-foreground">
            <span>Registered beds</span>
            <span>{intro ? "First year" : "Per year"}</span>
            <span>Standard renewal</span>
            <span className="w-[136px]" />
          </div>
          {data.bands.map((b, i) => <BandRow key={b.key} band={b} intro={intro} i={i} onStart={start} />)}
          <ContactRow />
        </div>
        <p className="text-sm text-muted-foreground mt-4 max-w-3xl">Introductory first-year prices apply to new care homes joining KOHC by {deadline}. Your standard renewal price is shown upfront, so there are no surprises.</p>
      </section>

      <section className="bg-brand text-white py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <span className="label-caps text-coral">Everything is included</span>
          <h2 className="text-2xl md:text-3xl font-bold mt-3 mb-10">Every KOHC Care Home Licence includes:</h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {INCLUDED.map((f, i) => (
              <motion.li key={f} initial={RISE_SM.initial} whileInView={RISE_SM.animate} viewport={ONCE} transition={{ delay: 0.04 * i }}
                data-testid={`included-${i}`} className="flex items-start gap-3 rounded-2xl bg-white/5 border border-white/10 p-5">
                <CheckCircle2 className="h-5 w-5 text-coral flex-shrink-0 mt-0.5" /><span className="text-white/90">{f}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div data-testid="explainer-fair" className="rounded-3xl bg-cream-card border border-cream-deep p-8 md:p-10">
          <div className="h-11 w-11 rounded-xl bg-brand-light grid place-items-center mb-5"><Scale className="h-5 w-5 text-brand" /></div>
          <h3 className="text-xl md:text-2xl font-bold text-brand">Fair pricing for every size of care home</h3>
          <p className="text-muted-foreground mt-4 leading-relaxed">A 20-bed care home shouldn't have to pay the same as a 100-bed care home.</p>
          <p className="text-muted-foreground mt-3 leading-relaxed">That's why KOHC pricing is based on registered bed capacity, rather than the number of staff using the platform.</p>
          <p className="text-muted-foreground mt-3 leading-relaxed">Your whole team can access KOHC throughout the year, including new starters, without additional per-person training fees.</p>
        </div>
        <div data-testid="explainer-unlimited" className="rounded-3xl bg-cream-card border border-cream-deep p-8 md:p-10">
          <div className="h-11 w-11 rounded-xl bg-brand-light grid place-items-center mb-5"><Users className="h-5 w-5 text-brand" /></div>
          <h3 className="text-xl md:text-2xl font-bold text-brand">One home. Unlimited staff.</h3>
          <p className="text-muted-foreground mt-4 leading-relaxed">There are no per-user charges and no need to purchase additional training places as your team changes.</p>
          <p className="text-muted-foreground mt-3 leading-relaxed">Your licence covers your whole care team at one registered care home for the entire year.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-20">
        <div data-testid="plan-tt_course" className="rounded-3xl border border-cream-deep bg-cream-card p-8 md:p-10 flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-start gap-4 max-w-2xl">
            <div className="h-11 w-11 rounded-xl bg-brand-light grid place-items-center flex-shrink-0"><GraduationCap className="h-5 w-5 text-brand" /></div>
            <div>
              <span className="label-caps text-muted-foreground">Separate premium qualification</span>
              <h3 className="text-xl md:text-2xl font-bold text-brand mt-1">Train the Trainer</h3>
              <p className="text-muted-foreground mt-2">Application-gated cohort with its own certificate and CPD. Not part of the Care Home Licence.</p>
            </div>
          </div>
          <div className="text-left md:text-right">
            <div className="text-3xl font-bold font-heading text-brand">{tt ? gbp(tt.amount) : "£750"}<span className="text-sm text-muted-foreground font-body font-normal"> one-off</span></div>
            <Button data-testid="plan-cta-tt_course" onClick={() => nav("/train-the-trainer")} variant="outline" className="mt-3 rounded-full border-brand text-brand hover:bg-brand hover:text-white transition-colors duration-200">Apply now</Button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24">
        <div data-testid="pricing-cta" className="rounded-3xl bg-brand text-white p-8 md:p-12 flex flex-wrap items-center justify-between gap-8">
          <div className="max-w-2xl">
            <span className="label-caps text-coral">Join KOHC at our introductory rate</span>
            <h2 className="text-2xl md:text-4xl font-bold mt-3">From {fromPrice} for your whole care team for the first year.</h2>
            {intro && <p className="text-white/70 mt-3">Introductory offer available to new care homes joining by {deadline}.</p>}
          </div>
          <Button data-testid="pricing-cta-btn" onClick={start} className="h-12 px-8 rounded-full bg-coral hover:bg-coral-hover text-white text-base">Get started <ArrowRight className="h-4 w-4 ml-2" /></Button>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
